import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  Badge,
  EmptyState,
  InputSearch,
  Panel,
  Progress,
  SegmentedControl,
  cn,
  PRESS,
  formatDate,
  formatMoney,
} from 'escrowbuild-ui';

import { useFixtureVocabulary, useT, type AppTranslationKey } from '@/i18n';
import { PROJECTS, projectTotals, type Project } from '@/data/domain';

/* -----------------------------------------------------------------------------
   The portfolio register (§6/§9)
   -----------------------------------------------------------------------------
   Every money figure is read from `projectTotals` rather than from the project
   record, because the record stores `actual` and the totals derive it from the
   schedule. Deriving once means this table cannot disagree with the project
   screen it links to — the one comparison a client makes during a walkthrough.
   -------------------------------------------------------------------------- */

type SortDirection = 'asc' | 'desc';

type ColumnKey =
  | 'name'
  | 'address'
  | 'phase'
  | 'progress'
  | 'contributed'
  | 'actual'
  | 'available'
  | 'variance'
  | 'projectedSale';

/*
   The three wrapping columns carry a width FLOOR, and the other six size to
   their content. It is a `min-width` rather than a `width` on purpose: an
   auto-layout table treats a cell width as a preference it is free to shrink
   when the table is over budget, which squeezed the project name to three
   lines while the progress column took a quarter of the table for a bar and a
   caption. A minimum is a floor the algorithm has to honour, so the name, the
   address and the bar each keep enough room to break over two lines.
*/
const MIN_PROJECT = 'min-w-[7rem]';
const MIN_ADDRESS = 'min-w-[7rem]';
const MIN_PROGRESS = 'min-w-[8.5rem]';

interface Column {
  key: ColumnKey;
  header: AppTranslationKey;
  /** Money and figures are right-aligned and set in tabular figures. */
  numeric?: boolean;
  /** Carried by the column's header cell and every one of its body cells. */
  minWidth?: string;
}

const COLUMNS: Column[] = [
  { key: 'name', header: 'nav.fallback', minWidth: MIN_PROJECT },
  { key: 'address', header: 'projects.column.address', minWidth: MIN_ADDRESS },
  { key: 'phase', header: 'projects.phase' },
  { key: 'progress', header: 'projects.column.progress', minWidth: MIN_PROGRESS },
  /* The key is `contributed`, so the header says so. It previously read
     "Committed" over the contributed figure, and those are different sums. */
  { key: 'contributed', header: 'project.shell.contributed', numeric: true },
  { key: 'actual', header: 'projects.column.spent', numeric: true },
  { key: 'available', header: 'projects.column.balance', numeric: true },
  { key: 'variance', header: 'projects.column.variance', numeric: true },
  { key: 'projectedSale', header: 'projects.column.projectedSale', numeric: true },
];

/** Which glyph a column shows, and whether the sort is on it at all. */
const SORT_GLYPH: Record<SortDirection | 'none', string> = {
  asc: '▲',
  desc: '▼',
  none: '↕',
};

const TH = 'bg-surface-alt px-3 py-2 text-left font-display text-2xs font-semibold uppercase tracking-label text-muted';
const TH_NUMERIC = 'bg-surface-alt px-3 py-2 text-right font-display text-2xs font-semibold uppercase tracking-label text-muted';
const TD = 'px-3 py-2 text-body';
/** A currency figure broken across two lines reads as two figures. */
const TD_NUMERIC = 'px-3 py-2 text-right whitespace-nowrap tabular-nums text-body';

/** The filter bar's own caption — `SegmentedControl`'s label names the group only. */
const FILTER_LABEL =
  'mb-1.5 block font-display text-2xs font-semibold uppercase tracking-label text-muted';

const ROW_LINK = cn(
  'font-medium text-heading underline decoration-line-strong underline-offset-2',
  'transition-colors duration-[var(--dur-fast)] hover:decoration-brand',
  PRESS,
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
);

interface PortfolioRow {
  project: Project;
  totals: ReturnType<typeof projectTotals>;
}

/**
 * The accessible name of a sort control states the column and what pressing it
 * does next, because the arrow glyph is `aria-hidden` and the direction is
 * therefore invisible to a screen reader. It opens with the visible header text
 * so the name still contains the label a sighted user reads.
 *
 * The three sentences arrive already translated: this is not a component, so it
 * has no `t`, and wording held here would be a second translation to keep in
 * step.
 */
function sortLabel(
  header: string,
  direction: SortDirection | 'none',
  sentences: { ascending: string; descending: string; none: string },
): string {
  if (direction === 'asc') return `${header}. ${sentences.ascending}`;
  if (direction === 'desc') return `${header}. ${sentences.descending}`;
  return `${header}. ${sentences.none}`;
}

function compare(a: PortfolioRow, b: PortfolioRow, key: ColumnKey): number {
  switch (key) {
    case 'name':
      return a.project.name.localeCompare(b.project.name);
    case 'address':
      return `${a.project.address} ${a.project.city}`.localeCompare(
        `${b.project.address} ${b.project.city}`,
      );
    case 'phase':
      return a.project.phase.localeCompare(b.project.phase);
    case 'progress':
      return a.project.progress - b.project.progress;
    case 'contributed':
      return a.totals.contributed - b.totals.contributed;
    case 'actual':
      return a.totals.actual - b.totals.actual;
    case 'available':
      return a.totals.available - b.totals.available;
    case 'variance':
      return a.totals.variance - b.totals.variance;
    case 'projectedSale':
      // ISO dates sort chronologically as strings.
      return a.project.projectedSale.localeCompare(b.project.projectedSale);
  }
}

/** Six-figure sums read better without cents in a comparison table. */
const money = (value: number) => formatMoney(value, { precision: 0 });

export function ProjectsPage() {
  const t = useT();
  const vocabulary = useFixtureVocabulary();
  const [term, setTerm] = useState('');
  const [phase, setPhase] = useState('all');
  const [sort, setSort] = useState<{ key: ColumnKey; direction: SortDirection } | null>(null);

  const portfolio = useMemo<PortfolioRow[]>(
    () => PROJECTS.map((project) => ({ project, totals: projectTotals(project.id) })),
    [],
  );

  const rows = useMemo(() => {
    const needle = term.trim().toLowerCase();
    const matched = portfolio.filter(
      ({ project }) =>
        (phase === 'all' || project.phase === phase) &&
        (needle === '' ||
          project.name.toLowerCase().includes(needle) ||
          project.address.toLowerCase().includes(needle) ||
          project.city.toLowerCase().includes(needle)),
    );

    if (!sort) return matched;
    const ordered = [...matched].sort((a, b) => compare(a, b, sort.key));
    return sort.direction === 'desc' ? ordered.reverse() : ordered;
  }, [portfolio, term, phase, sort]);

  const filtersActive = term.trim() !== '' || phase !== 'all';

  const clearFilters = useCallback(() => {
    setTerm('');
    setPhase('all');
  }, []);

  /** asc → desc → none, so a third press returns the portfolio to its own order. */
  const toggleSort = useCallback((key: ColumnKey) => {
    setSort((current) =>
      current?.key !== key
        ? { key, direction: 'asc' }
        : current.direction === 'asc'
          ? { key, direction: 'desc' }
          : null,
    );
  }, []);

  // Built here because the unfiltered option's label needs `t`; the phase names
  // beside it are dataset values.
  /*
     Every phase in use, derived from the portfolio rather than listed by hand, so
     a fourth development whose phase is new appears here without anyone
     remembering to add it — and resolved here rather than at module scope, so the
     facet follows the language selector like everything else on the page.

     The facet's VALUE is the dataset's own phase name, which is what each row is
     filtered on; only the label a reader sees is translated, so the control and
     the rows it matches can never disagree about which phase they mean. The
     options are ordered by the word on screen, which is the only order a reader
     can check.
  */
  const phaseOptions = useMemo(() => {
    const names = [...new Set(PROJECTS.map((project) => project.phase))].map((name) => ({
      value: name,
      label: vocabulary.phase(name),
    }));
    return [
      { value: 'all', label: t('common.all') },
      ...names.sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [t, vocabulary]);

  const sortSentences = {
    ascending: t('projects.sort.asc'),
    descending: t('projects.sort.desc'),
    none: t('projects.sort.none'),
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="font-display text-3xl font-semibold uppercase tracking-title text-heading">
          {t('projects.title')}
        </h1>
        <p className="mt-1 text-sm text-muted">
          {t('projects.subtitle', { count: PROJECTS.length })}
        </p>
      </div>

      <Panel title={t('projects.panelTitle')} description={t('projects.panelDescription')}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
            <div className="w-full min-w-0 max-w-xs">
              <InputSearch
                id="portfolio-search"
                label={t('projects.searchLabel')}
                value={term}
                onChange={setTerm}
                onSearch={setTerm}
                placeholder={t('projects.searchPlaceholder')}
                debounceMs={200}
              />
            </div>

            <div className="flex min-w-0 flex-col">
              <span className={FILTER_LABEL}>{t('projects.phase')}</span>
              {/* The segments never wrap, so the track scrolls rather than pushing
                  the page sideways on a narrow viewport. */}
              <div className="max-w-full overflow-x-auto">
                <SegmentedControl
                  options={phaseOptions}
                  value={phase}
                  onValueChange={setPhase}
                  size="sm"
                  label={t('projects.phase')}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p role="status" className="text-xs text-muted">
              {t('projects.matched', { count: PROJECTS.length, matched: rows.length })}
            </p>
            {filtersActive && rows.length > 0 ? (
              <button
                type="button"
                onClick={clearFilters}
                className={cn(
                  'rounded-xs px-2 py-1 text-xs font-medium text-body underline decoration-line-strong underline-offset-2',
                  'transition-colors duration-[var(--dur-fast)] hover:text-heading',
                  PRESS,
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                )}
              >
                {t('projects.clearFilters')}
              </button>
            ) : null}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="dhd-sr-only">{t('projects.caption')}</caption>

              <thead>
                <tr className="border-b-2 border-line-strong">
                  {COLUMNS.map((column) => {
                    const active = sort?.key === column.key;
                    const direction = active ? sort.direction : 'none';
                    const header = t(column.header);
                    return (
                      <th
                        key={column.key}
                        scope="col"
                        aria-sort={
                          direction === 'asc'
                            ? 'ascending'
                            : direction === 'desc'
                              ? 'descending'
                              : 'none'
                        }
                        className={cn(column.numeric ? TH_NUMERIC : TH, column.minWidth)}
                      >
                        <button
                          type="button"
                          onClick={() => toggleSort(column.key)}
                          aria-label={sortLabel(header, direction, sortSentences)}
                          className={cn(
                            'flex w-full items-center gap-1.5 whitespace-nowrap rounded-xs',
                            column.numeric ? 'justify-end' : 'justify-start',
                            PRESS,
                            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                          )}
                        >
                          <span>{header}</span>
                          <span
                            aria-hidden="true"
                            className={cn(
                              'text-xs leading-none',
                              active ? 'text-heading' : 'text-muted',
                            )}
                          >
                            {SORT_GLYPH[direction]}
                          </span>
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  // Kept inside the table so the headers stay visible, and the reader
                  // can see which columns the empty result belongs to.
                  <tr>
                    <td colSpan={COLUMNS.length} className="p-0 align-top">
                      <EmptyState
                        variant="table"
                        title={t('projects.empty.title')}
                        description={t('projects.empty.description', { count: PROJECTS.length })}
                        filtersActive={filtersActive}
                        onClearFilters={clearFilters}
                      />
                    </td>
                  </tr>
                ) : null}

                {rows.map(({ project, totals }) => (
                  <tr key={project.id} className="border-b border-line-subtle hover:bg-surface-alt">
                    <td className={cn(TD, MIN_PROJECT)}>
                      <Link to={`/projects/${project.id}`} className={ROW_LINK}>
                        {project.name}
                      </Link>
                    </td>

                    <td className={cn(TD, MIN_ADDRESS)}>
                      {/* City inline and unbreakable: as its own block it made every
                          row three lines tall the moment the street wrapped, and
                          left as breakable text it stranded the state code alone on
                          a line under the town. */}
                      {project.address}{' '}
                      <span className="whitespace-nowrap text-muted">· {project.city}</span>
                    </td>

                    <td className={TD}>
                      <Badge tone="info" variant="subtle" size="sm">
                        {vocabulary.phase(project.phase)}
                      </Badge>
                    </td>

                    <td className={cn(TD, MIN_PROGRESS)}>
                      {/* The label is the bar's accessible name, and in a table cell
                          it is also the only thing that says which row it measures.
                          It is kept to the project's own name because the column
                          header already supplies the word "progress", and a longer
                          label here costs every other column its width. */}
                      <Progress
                        value={project.progress}
                        size="sm"
                        label={project.name}
                        showValue
                      />
                    </td>

                    <td className={TD_NUMERIC}>{money(totals.contributed)}</td>
                    <td className={TD_NUMERIC}>{money(totals.actual)}</td>
                    <td className={TD_NUMERIC}>{money(totals.available)}</td>

                    {/* The sign is carried by the money itself, so the figure
                        still reads under or over budget with the colour ignored. */}
                    <td
                      className={cn(
                        TD_NUMERIC,
                        totals.variance <= 0 ? 'text-success' : 'text-danger',
                      )}
                    >
                      {totals.variance > 0 ? '+' : ''}
                      {money(totals.variance)}
                    </td>

                    <td className={TD_NUMERIC}>
                      {formatDate(project.projectedSale, { style: 'medium' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Panel>
    </div>
  );
}
